import {MEDIA_CDN_HOST} from '@aboutyou/config';
import {useLegacyGrpcRequest} from '@aboutyou/data-fetcher';
/* eslint-disable monorepo/no-internal-import */
import {useLegacyGrpcClientConfig} from '@aboutyou/data-fetcher/src/grpc/GrpcClientContext';
import {GripsDevice} from '@aboutyou/data-fetcher/src/grpc/useLegacyGrpcRequest';
import {Placement_LinkTarget} from '@aboutyou/grpc-clients/grips/protos/promotions/v1/campaign.types';
import {PromotionsService_getOnSiteCampaignLazy} from '@aboutyou/grpc-clients/grips/protos/promotions/v1/promotions_service.grpc.lazy';
import {useIsLoggedIn, useUserToken} from '@aboutyou/user';
import {useTargetGroupIdentifier} from './useTargetGroupIdentifier';

const PLACEMENT_KEY = 'searchbar_bas';

interface SearchBarPromotionDataRequest {
  isRequestDisabled?: boolean;
}

const useFetchSearchPromotionData = ({
  isRequestDisabled,
}: SearchBarPromotionDataRequest) => {
  const targetGroupIdentifier = useTargetGroupIdentifier();
  const userIsLoggedIn = useIsLoggedIn();
  const authToken = useUserToken() ?? '';
  const {shopId, childShopId, device} = useLegacyGrpcClientConfig();

  return useLegacyGrpcRequest(
    PromotionsService_getOnSiteCampaignLazy,
    {
      config: {
        shopId,
        childShopId,
        device: (device === 'desktop'
          ? GripsDevice.DESKTOP
          : GripsDevice.MOBILE_WEB) as number,
        session: userIsLoggedIn
          ? {
              $case: 'customerSession',
              customerSession: {authToken},
            }
          : {$case: 'guestSession', guestSession: {sessionId: authToken}},
      },
      gender: targetGroupIdentifier as number,
      page: {
        type: PLACEMENT_KEY,
      },
    },
    {disabled: Boolean(isRequestDisabled)},
  );
};

export const useSearchBarPromotionData = ({
  isRequestDisabled,
}: SearchBarPromotionDataRequest) => {
  const {device} = useLegacyGrpcClientConfig();
  const {data} = useFetchSearchPromotionData({isRequestDisabled});
  const campaign = data?.campaigns[0];
  const placement = campaign?.placements[PLACEMENT_KEY];

  return {
    promotionData: placement
      ? {
          image: `${MEDIA_CDN_HOST}${placement.images[device]}`,
          target: getBasLink(placement.target),
          promotionTracking: {id: String(campaign.id), name: campaign.name},
        }
      : undefined,
  };
};

const getBasLink = (target?: Placement_LinkTarget) => {
  let url = '';
  let linkTarget = '_self';
  let external = false;

  if (target) {
    switch (target.page?.$case) {
      case 'brand':
        url = `/b/shop/brand-${target.page.brand.id}`;
        break;
      case 'external':
        url = target.page.external.url;
        linkTarget = target.page.external.webViewOrSameTab ? '_self' : '_blank';
        external = true;
        break;
      case 'outfit':
        url = `/o/outfit-${target.page.outfit.id}`;
        break;
      case 'story':
        url = `/s/story-${target.page.story.id}`;
        break;
    }
  }

  return {
    url,
    target: linkTarget,
    external,
  };
};
